/**
 * @file Vuex getters for the tools store, which handles navigation, including search.
 * @module src/tools/store/getters
 */

import _ from 'underscore'
import dataFreshness from 'src/layout/store/utils/dataFreshness'
import storage from 'src/store/storage'
import PATHS from 'src/router/paths'
import uniquify from 'src/utils/uniquify'
import { copyObject, createObject } from '@/utils/objects'
import { createReportId, createReportRole, pushRole } from '@/report/store/utils/report'
import calculate from '@/utils/calculate'

const GETTERS = {
  reportClass(state) {
    return state.baseReportName && state.baseReportName.replace(/\//g, '-')
  },

  paths(state) {
    const { user } = state
    const { customAnalyticsActive, hasCustomAnalytics } = user || {}
    const buildPaths = () => customAnalyticsActive.reduce((paths, customAnalytic) => {
      const { href } = customAnalytic
      const key = href.toUpperCase().replace(/-/g, '_')

      paths[key] = href

      return paths
    }, PATHS)

    return (hasCustomAnalytics && buildPaths()) || PATHS
  },
  routeMap(state, getters) {
    const { paths } = getters

    return Object.keys(paths).reduce((baseRouteNames, baseRouteName) => {
      const baseRoute = paths[baseRouteName]

      Object.keys(baseRoute).forEach((subRouteName) => {
        const currentPath = baseRoute[subRouteName]
        baseRouteNames[currentPath] = baseRoute.BASE
      })

      return baseRouteNames
    }, {})
  },

  validRouteNames(state, getters) {
    return Object.keys(getters.routeMap)
  },
  currentRouteIsAllowed(state, getters) {
    return getters.validRouteNames.indexOf(state.routeName) !== -1
  },
  currentReportIsAllowed(state, getters) {
    const { baseReportName, routeName } = state
    const { currentRouteIsAllowed, routeMap } = getters
    const isReportAllowed = () => !baseReportName || (baseReportName && routeMap[baseReportName] === routeName)
    return currentRouteIsAllowed && isReportAllowed()
  },

  // Page > Route > Component
  // Component !== BaseReportName because of Time-to-TimePage rewrite for browsers.

  currentPageName(state) {
    return state.baseReportName || state.routeName
  },
  currentRouteName(state, getters) {
    const { currentReportIsAllowed } = getters

    return currentReportIsAllowed && state.routeName
  },
  currentComponentName(state, getters) {
    const { currentRouteName, currentPageName } = getters
    const getCustomAnalyticComponent = () => currentRouteName === 'custom-analytics' && currentRouteName

    return getCustomAnalyticComponent() || currentPageName
  },

  currentViewId(state, getters) {
    const { viewIds } = state
    const { currentPageName } = getters

    return !!currentPageName && viewIds[currentPageName]
  },

  currentDefaultColumns(state, getters) {
    const { columnDefaults = {} } = state
    const { currentPageName } = getters

    return !!currentPageName && columnDefaults[currentPageName]
  },
  currentColumns(state, getters) {
    const { columns = {} } = state
    const { currentPageName } = getters

    return !!currentPageName && columns[currentPageName]
  },
  currentRequiredColumns(state, getters) {
    return state.requiredColumns[getters.currentPageName] || []
  },
  reportColumns(state, getters) {
    const { currentColumns, currentRequiredColumns } = getters

    return currentColumns?.length && currentColumns.map((column) => {
      const isRequiredColumn = currentRequiredColumns.indexOf(column.key) !== -1
      const undefinedColumnVisible = typeof column.visible !== 'boolean'
      const setVisible = undefinedColumnVisible || isRequiredColumn
      const columnClasses = (typeof column.class !== 'function' && column.class) || column.key

      column.classes = `${columnClasses}${column.frozen ? ' column-sticky' : ''} text-${column.align}`
      column.leftPx = (column.frozen && `${column.left}px`) || 'auto'
      column.widthPx = `${column.width}px`
      column.minWidthPx = `${column.minWidth}px`
      column.widthStyles = `${column.minWidth ? `min-width: ${column.minWidthPx}; ` : ''}${column.width ? `width: ${column.widthPx};` : ''}`
      column.styles = {
        left: column.leftPx,
        minWidth: column.minWidthPx,
        width: column.widthPx,
      }

      column.required = isRequiredColumn
      column.visible = setVisible || column.visible

      column.text = column.columnName
      column.value = column.key

      const sortIndex = getters.sortByKeys.findIndex((key) => key === column.key)

      if (sortIndex !== -1) {
        const currentDirection = getters.sortByDirections[sortIndex]
        column.sortDsc = currentDirection
        column.sortIcon = state.sortIcons[currentDirection]
        column.sortClassIndicator = `brightblue--text ${state.sortClassIndicatorMap[column.align]}`
        column.sortClassText = state.sortClassTextMap[column.align]
        column.sortIndex = sortIndex + 1
      } else {
        column.sortDsc = state.tableSortMap.ASC
        column.sortIcon = state.sortIcons.ASC
        column.sortClassIndicator = `midgray--text ${state.sortClassIndicatorMap[column.align]}`
        column.sortClassText = state.sortClassTextMap[column.align]
        column.sortIndex = -1
      }

      return column
    }).sort((a, b) => a.index - b.index)
  },
  tableColumns(state, getters) {
    return (getters.reportColumns || []).filter((col) => !col.exportOnly)
  },
  visibleColumns(state, getters) {
    return getters.tableColumns.filter((col) => col.visible)
  },
  frozenColumns(state, getters) {
    return getters.visibleColumns.filter((header) => header.frozen)
  },

  columnsByKeyReducer() {
    return (reportColumns) => reportColumns.reduce((columns, column) => {
      columns[column.key] = column
      return columns
    }, {})
  },
  columnsByKey(state, getters) {
    return getters.columnsByKeyReducer(getters.tableColumns || [])
  },
  currentDefaultColumnsByKey(state, getters) {
    return getters.columnsByKeyReducer(getters.currentDefaultColumns || [])
  },
  orgColumnsByKey(state, getters) {
    return getters.columnsByKeyReducer(state.columns.organization || [])
  },
  timeRollupColumnsByKey(state, getters) {
    return getters.columnsByKeyReducer(state.columns[PATHS.INTERNAL.TIME_ROLLUP] || [])
  },
  personColumnsByKey(state, getters) {
    return getters.columnsByKeyReducer(state.columns.person || [])
  },
  projectColumnsByKey(state, getters) {
    return getters.columnsByKeyReducer(state.columns.project || [])
  },
  serviceColumnsByKey(state, getters) {
    return getters.columnsByKeyReducer(state.columns.service || [])
  },
  timeColumnsByKey(state, getters) {
    return getters.columnsByKeyReducer(state.columns[PATHS.INTERNAL.TIME_SHEET] || [])
  },
  timeTypeColumnsByKey(state, getters) {
    return getters.columnsByKeyReducer(state.columns.timeType || [])
  },

  currentGroupByProperties(state, getters) {
    const { groupByProperties } = state
    const { currentPageName } = getters

    return groupByProperties[currentPageName] || []
  },
  currentGrouping(state, getters) {
    const { grouping } = state
    const { currentPageName } = getters

    return grouping[currentPageName] || []
  },
  currentGroupingOptions(state, getters) {
    const { groupingOptions } = state
    const { currentPageName } = getters

    return groupingOptions[currentPageName] || []
  },

  currentMergeByProperties(state, getters) {
    const { mergeByProperties } = state
    const { currentPageName } = getters

    return mergeByProperties[currentPageName] || []
  },
  currentMerging(state, getters) {
    const { merging } = state
    const { currentPageName } = getters

    return merging[currentPageName] || []
  },
  currentMergingOptions(state, getters) {
    const { mergingOptions } = state
    const { currentPageName } = getters

    return mergingOptions[currentPageName] || []
  },

  currentSorted(state, getters) {
    const { sorted } = state
    const { currentPageName } = getters

    return sorted[currentPageName]
  },
  currentSorting(state, getters) {
    const { sorting } = state
    const { currentPageName } = getters

    return sorting[currentPageName]
  },
  currentlySorting(state, getters) {
    const { isSorting } = state
    const { currentPageName } = getters

    return isSorting[currentPageName]
  },

  sortByDirections(state, getters) {
    return state.sortingByDirections || getters.currentSorting?.map((v) => state.tableSortMap[v.direction]) || []
  },
  sortByKeys(state, getters) {
    return state.sortingByKeys || getters.currentSorting?.map((v) => v.key) || []
  },

  currentFilterData(state, getters) {
    const { filterData } = state
    const { currentPageName } = getters

    return filterData[currentPageName]
  },
  currentNumberOfAppliedFilters(state, getters) {
    const { numberOfAppliedFilters } = state
    const { currentPageName } = getters

    return numberOfAppliedFilters[currentPageName]
  },

  hasNoReport(state, getters) {
    const { currentComponentName, isAssociationIdRequiredOnRoute, teamsByReportCode } = getters
    const { accessFields, associationId, subId } = state
    const reportCode = associationId && `${associationId}/${subId || '-1'}`
    const currentTeam = reportCode && teamsByReportCode[reportCode]

    return isAssociationIdRequiredOnRoute && currentTeam && !currentTeam[accessFields[currentComponentName]]
  },
  isLoadingContent(state, getters, rootState, rootGetters) {
    const { currentColumns, currentItems, currentViewId, hasNoReport, isCustomAnalyticRoute, isDocsRoute } = getters
    const currentMessage = rootGetters['messages/currentMessage']
    const isCurrentMessageActive = rootGetters['messages/isCurrentMessageActive']

    const requiresLoading = () => !isCustomAnalyticRoute && !isDocsRoute && !hasNoReport
    const isPendingSetup = () => ((!currentViewId && 'setup-view') || (!currentColumns && 'setup-columns') || (!currentMessage && 'setup-message'))
    const isLoadingData = () => currentItems === undefined && 'data'
    const isMessaging = () => isCurrentMessageActive && 'message'

    return requiresLoading() && (isPendingSetup() || isLoadingData() || isMessaging())
  },

  storedItems() {
    return storage.get('ITEMS')
  },
  storedFilteredGroupedMergedItems() {
    return storage.get('REPORT_RESULTS')
  },

  currentAction(state) {
    const { query } = state
    return (query && query.action) || 'read'
  },
  currentSelectedReport(state, getters) {
    const { reportPath } = state
    const { reportOptionsForSelectedTeam } = getters
    return (reportPath && reportOptionsForSelectedTeam.find((reportOption) => reportOption.reportPath === reportPath || reportOption.docId === reportPath)) || null
  },
  currentReportKeyData(state, getters) {
    const { currentPageName, currentViewId, storedItems } = getters
    const hasRequirements = !!currentViewId && !!storedItems && !!currentPageName
    const dataType = 'ITEMS'

    return hasRequirements && storage.getEndpoint(dataType, currentPageName)
  },
  currentReportKeyDataAll(state, getters) {
    const { currentPageName } = getters
    const dataType = 'ITEMS'
    const { associationId, subId = 'all-secondary' } = state
    const reportPath = 'all'
    return storage.getPath(dataType, currentPageName, associationId, subId, reportPath)
  },
  currentReportKeyItems(state, getters) {
    return getters.currentReportKeyData || []
  },
  currentReportItems(state, getters) {
    const { reportPath, associationId, subId = 'all-secondary' } = state
    const { currentPageName, currentReportKeyData } = getters
    const dataType = 'ITEMS'
    const haveEndpointData = !!(associationId && currentReportKeyData)
    const getPath = () => storage.getPath(dataType, currentPageName, associationId, subId, reportPath)
    const getAll = () => storage.getAll(dataType, currentPageName)

    return (haveEndpointData && getPath()) || getAll()
  },
  currentItems(state, getters) {
    return getters.currentReportItems || []
  },
  currentReportGroups(state, getters) {
    const { reportPath, associationId, routeName, subId = 'all-secondary' } = state
    const { currentPageName, currentViewId, storedFilteredGroupedMergedItems: storedData } = getters
    const haveEndpointData = !!(currentViewId && currentPageName && storedData && storedData[currentPageName])
    const dataType = 'REPORT_RESULTS'

    const getReportGroups = () => {
      if (associationId && (associationId !== 'all-associations' || routeName === 'internal')) {
        return storage.getPath(dataType, currentPageName, associationId, subId, reportPath) || []
      }
      return storage.getAll(dataType, currentPageName) || []
    }
    const flatten = (reportGroups) => reportGroups.reduce((flattenedGroups, reportGroup) => {
      const hasGroupInside = reportGroup.items.find((item) => item.isPartOfGroup)

      flattenedGroups.push(reportGroup)

      if (hasGroupInside) {
        flattenedGroups.push(...reportGroup.items)
      }

      return flattenedGroups
    }, [])
    const currentReportGroups = haveEndpointData && flatten(getReportGroups())

    return currentReportGroups || []
  },
  currentReportMetrics(state, getters, rootState) {
    const { metricGroupByMap, metricTypeMap } = rootState.dashboard
    const { associationId, reportPath, subId = 'all-secondary' } = state
    const { currentPageName, currentViewId, reportOptionsForSelectedTeam, storedFilteredGroupedMergedItems: storedData } = getters
    const haveEndpointData = !!(currentViewId && currentPageName && storedData && storedData[currentPageName])
    const dataType = 'METRICS'

    const getMetrics = () => storage.getAssociationAndSubId(dataType, currentPageName, associationId, subId)
    const metricsByReportPath = haveEndpointData && getMetrics()
    const metricsForCurrentReport = metricsByReportPath && metricsByReportPath[reportPath]
    const haveData = metricsForCurrentReport && reportOptionsForSelectedTeam.length

    const reportMetrics = haveData && reportOptionsForSelectedTeam
      .filter((reportOption) => reportOption.isMetric)
      .sort((a, b) => a.index - b.index)
      .map((reportOption) => {
        const newReportOption = copyObject(reportOption)
        const currentMetricItems = metricsForCurrentReport[reportOption.reportPath] || []
        const [defaultMetricGroupBy] = metricGroupByMap[currentPageName]
        const [defaultMetricType] = metricTypeMap[currentPageName]

        newReportOption.items = currentMetricItems

        newReportOption.metricGroupBy = defaultMetricGroupBy
        newReportOption.metricType = defaultMetricType

        newReportOption[newReportOption.metricType.value] = calculate[newReportOption.metricType.value](newReportOption.items)
        newReportOption[newReportOption.metricGroupBy.value] = calculate[newReportOption.metricGroupBy.value](newReportOption.items)

        return newReportOption
      })

    return reportMetrics || []
  },
  currentMetric(state, getters) {
    const { currentReportMetrics } = getters
    const { metricPath } = state
    return currentReportMetrics.find((report) => report.reportPath === metricPath || report.docId === metricPath)
  },
  currentMetricGroups(state, getters) {
    const { currentMetric } = getters
    return currentMetric && currentMetric.items
  },

  currentGroups(state, getters) {
    const { associationId } = state
    const { currentReportGroups, currentViewId, isAssociationIdRequiredOnRoute } = getters

    return (!!currentViewId && (!isAssociationIdRequiredOnRoute || associationId) && currentReportGroups) || []
  },
  currentRowCount(state, getters) {
    return getters.currentGroups.reduce((rowCount, currentGroup) => {
      rowCount += currentGroup.items.length
      return rowCount
    }, 0)
  },

  currentSummaries(state, getters) {
    const { associationId, subId, summaries } = state
    const { currentPageName } = getters
    const reportSummaries = !!summaries && !!currentPageName && summaries[currentPageName]
    const getCurrentSummaries = () => {
      const defaultSummaries = reportSummaries?.defaults
      const getDefaultSummaries = () => !reportSummaries && defaultSummaries
      const getSummariesAtRoot = () => !associationId && reportSummaries
      const getSummariesAtAssociationId = () => subId === undefined && reportSummaries[associationId]
      const getSummariesAtSubId = () => reportSummaries[associationId] && reportSummaries[associationId][subId]

      return getDefaultSummaries() || getSummariesAtRoot() || getSummariesAtAssociationId() || getSummariesAtSubId() || defaultSummaries
    }
    const noResults = () => !reportSummaries && {}

    return noResults() || getCurrentSummaries()
  },
  currentSummary(state, getters) {
    const { currentSummaries = {} } = getters
    const summaryNames = Object.keys(currentSummaries)

    const currentSummaryName = summaryNames.find((summaryName) => {
      const { isActive } = currentSummaries[summaryName]

      return isActive
    })

    return currentSummaryName ? currentSummaries[currentSummaryName] : null
  },

  getSearchOptions() {
    return (pageName, searchResults) => {
      const searchOptions = searchResults && searchResults.endpoints[pageName]
      return (searchOptions && searchOptions.itemsArray) || []
    }
  },
  searchOptions(state, getters) {
    const { searchResults } = state
    const { currentPageName } = getters

    return getters.getSearchOptions(currentPageName, searchResults)
  },

  findSearchOption() {
    return (searchOptions, associationId, baseReportName, reportPath, routeName, subId) => searchOptions && searchOptions.find((option) => option.associationId === associationId
      && option.baseReportName === baseReportName
      && option.routeName === routeName
      && option.subId === subId
      && (option.reportPath === reportPath || option.docId === reportPath))
  },
  selectedSearch(state, getters) {
    const { associationId = null, baseReportName = null, reportPath = null, routeName = null, subId = null } = state
    const { searchOptions } = getters
    const findSearchOption = () => getters.findSearchOption(searchOptions, associationId, baseReportName, reportPath, routeName, subId)

    return (!!associationId && findSearchOption()) || null
  },

  isAssociationIdBillingCode(state) {
    const { associationId } = state
    return /([A-Z]{4})([0-9]{4})\b/.test(associationId)
  },
  isAssociationIdNumerical(state) {
    const { associationId } = state
    return /^[0-9]+\b/.test(associationId)
  },
  isAssociationIdValid(state, getters) {
    const { isAssociationIdBillingCode, isAssociationIdNumerical } = getters
    return isAssociationIdBillingCode || isAssociationIdNumerical
  },
  async hasPacing(state, getters, rootState, rootGetters) {
    const isInternalUser = rootGetters['auth/isInternalUser']

    if (isInternalUser) return true

    const { searchData } = state
    const organizations = await searchData.organization

    if (!organizations) return false

    return organizations.find((publisher) => publisher.hasPacing)
  },
  async hasDiscrepancy(state, getters, rootState, rootGetters) {
    const isInternalUser = rootGetters['auth/isInternalUser']

    if (isInternalUser) return true

    const { searchData } = state
    const organizations = await searchData.organization

    if (!organizations) return false

    return organizations.find((publisher) => publisher.hasDiscrepancy)
  },
  async hasHealthAdx(state, getters, rootState, rootGetters) {
    const isInternalUser = rootGetters['auth/isInternalUser']

    if (isInternalUser) return true

    const { searchData } = state
    const publishers = await searchData.organization

    if (!publishers) return false

    return publishers.find((publisher) => publisher.hasHealthAdx)
  },
  async hasHealthNetwork(state, getters, rootState, rootGetters) {
    const isInternalUser = rootGetters['auth/isInternalUser']

    if (isInternalUser) return true

    const { searchData } = state
    const organizations = await searchData.organization

    if (!organizations) return false

    return organizations.find((publisher) => publisher.hasHealthNetwork)
  },
  async hasYieldPartner(state, getters, rootState, rootGetters) {
    const isInternalUser = rootGetters['auth/isInternalUser']

    if (isInternalUser) return true

    const { searchData } = state
    const organizations = await searchData.organization

    if (!organizations) return false

    return organizations.find((publisher) => publisher.hasYieldPartner)
  },
  async hasProgrammatic(state, getters, rootState, rootGetters) {
    const isInternalUser = rootGetters['auth/isInternalUser']

    if (isInternalUser) return true

    const { searchData } = state
    const organizations = await searchData.publisher

    if (!organizations) return false

    return organizations.find((publisher) => publisher.hasProgrammatic)
  },

  healthAdXFilterValues(state, getters) {
    const { currentPageName, currentItems } = getters

    const seeds = {
      deviceCategory: [],
      inventorySize: [],
      topLevelAdUnitCode: [],
      topLevelAdUnitName: [],
    }

    if (currentPageName !== 'adx-health') return seeds

    const tracker = {
      deviceCategory: [],
      inventorySize: [],
      topLevelAdUnitCode: [],
      topLevelAdUnitName: [],
    }
    const valueMap = {
      deviceCategory: {
        operator: 'incl',
        columnValue: (value) => value,
      },
      inventorySize: {
        operator: 'eq',
        columnValue: (value) => value,
      },
      topLevelAdUnitCode: {
        operator: 'eq',
        columnValue: (value) => value,
      },
      topLevelAdUnitName: {
        operator: 'incl',
        columnValue: (value) => value,
      },
    }

    const filters = Object.keys(seeds)

    return currentItems.reduce(uniquify.valueMap(filters, tracker, valueMap), seeds)
  },

  deviceCategoryFilterValues(state, getters) {
    const { healthAdXFilterValues } = getters
    const { deviceCategory } = healthAdXFilterValues

    return _.sortAscByCaseInsensitive(deviceCategory, 'value')
  },
  inventorySizeFilterValues(state, getters) {
    const { healthAdXFilterValues } = getters
    const { inventorySize } = healthAdXFilterValues

    return _.sortAscByCaseInsensitive(inventorySize, 'value')
  },

  healthNetworkFilterValues(state, getters) {
    const { currentPageName, currentItems } = getters

    const seeds = {
      topLevelAdUnitCode: [],
      topLevelAdUnitName: [],
      deviceCategoryName: [],
      requestedAdSizes: [],
    }

    if (currentPageName !== 'network-health') return seeds

    const tracker = {
      topLevelAdUnitCode: [],
      topLevelAdUnitName: [],
      deviceCategoryName: [],
      requestedAdSizes: [],
    }
    const valueMap = {
      topLevelAdUnitCode: {
        operator: 'eq',
        columnValue: (value) => value,
      },
      topLevelAdUnitName: {
        operator: 'incl',
        columnValue: (value) => value,
      },
      deviceCategoryName: {
        operator: 'incl',
        columnValue: (value) => value,
      },
      requestedAdSizes: {
        operator: 'eq',
        columnValue: (value) => value,
      },
    }

    const filters = Object.keys(seeds)

    return currentItems.reduce(uniquify.valueMap(filters, tracker, valueMap), seeds)
  },

  topLevelAdUnitCodeFilterValues(state, getters) {
    const { currentPageName, healthAdXFilterValues, healthNetworkFilterValues } = getters
    const filterValues = (currentPageName === 'network-health' && healthNetworkFilterValues) || healthAdXFilterValues
    const { topLevelAdUnitCode } = filterValues

    return _.sortAscByCaseInsensitive(topLevelAdUnitCode, 'value')
  },
  topLevelAdUnitNameFilterValues(state, getters) {
    const { currentPageName, healthAdXFilterValues, healthNetworkFilterValues } = getters
    const filterValues = (/network/.test(currentPageName) && healthNetworkFilterValues) || healthAdXFilterValues
    const { topLevelAdUnitName } = filterValues

    return _.sortAscByCaseInsensitive(topLevelAdUnitName, 'value')
  },
  deviceCategoryNameFilterValues(state, getters) {
    const { healthNetworkFilterValues } = getters
    const { deviceCategoryName } = healthNetworkFilterValues

    return _.sortAscByCaseInsensitive(deviceCategoryName, 'value')
  },
  requestedAdSizesFilterValues(state, getters) {
    const { healthNetworkFilterValues } = getters
    const { requestedAdSizes } = healthNetworkFilterValues

    return _.sortAscByCaseInsensitive(requestedAdSizes, 'value')
  },

  yieldFilterValues(state, getters) {
    const { currentPageName, currentItems } = getters

    const seeds = {
      demandChannelCode: [],
      demandChannelName: [],
      yieldPartner: [],
    }

    if (currentPageName !== 'yield-partner') return seeds

    const tracker = {
      demandChannelCode: [],
      demandChannelName: [],
      yieldPartner: [],
    }
    const valueMap = {
      demandChannelCode: {
        operator: 'eq',
        columnValue: (value) => value,
      },
      demandChannelName: {
        operator: 'incl',
        columnValue: (value) => value,
      },
      yieldPartner: {
        operator: 'incl',
        columnValue: (value) => value,
      },
    }

    const filters = Object.keys(seeds)

    return currentItems.reduce(uniquify.valueMap(filters, tracker, valueMap), seeds)
  },

  demandChannelCodeFilterValues(state, getters) {
    const { yieldFilterValues } = getters
    const { demandChannelCode } = yieldFilterValues

    return _.sortAscByCaseInsensitive(demandChannelCode, 'value')
  },
  demandChannelNameFilterValues(state, getters) {
    const { yieldFilterValues } = getters
    const { demandChannelName } = yieldFilterValues

    return _.sortAscByCaseInsensitive(demandChannelName, 'value')
  },
  yieldPartnerFilterValues(state, getters) {
    const { yieldFilterValues } = getters
    const { yieldPartner } = yieldFilterValues

    return _.sortAscByCaseInsensitive(yieldPartner, 'value')
  },

  deliveryFilterValues(state, getters) {
    const { deliveryRoutes } = state
    const { currentPageName, currentItems } = getters
    const notDeliveryReport = !deliveryRoutes.includes(currentPageName)

    const seeds = {
      advertiserName: [],
      creativeSizes: [],
      customFields: [],
      orderName: [],
      lineItemName: [],
      priority: [],
      salespersonName: [],
      secondaryTraffickerNames: [],
      status: [],
      topLevelAdUnitIds: [],
      topLevelAdUnitNames: [],
      traffickerName: [],
    }
    const tracker = {
      advertiserName: [],
      creativeSizes: [],
      customFields: [],
      orderName: [],
      lineItemName: [],
      priority: [],
      salespersonName: [],
      secondaryTraffickerNames: [],
      status: [],
      topLevelAdUnitIds: [],
      topLevelAdUnitNames: [],
      traffickerName: [],
    }
    const valueMap = {
      advertiserName: {
        operator: 'incl',
        columnValue: (value) => value,
      },
      creativeSizes: {
        operator: 'eq',
        columnValue: (value) => value,
      },
      customFields: {
        operator: 'incl',
        columnValue: (value) => value,
      },
      orderName: {
        operator: 'incl',
        columnValue: (value) => value,
      },
      lineItemName: {
        operator: 'incl',
        columnValue: (value) => value,
      },
      priority: {
        operator: 'incl',
        columnValue: (value) => value,
      },
      salespersonName: {
        operator: 'incl',
        columnValue: (value) => value,
      },
      secondaryTraffickerNames: {
        operator: 'incl',
        columnValue: (value) => value,
      },
      status: {
        operator: 'incl',
        columnValue: (value) => value,
      },
      topLevelAdUnitIds: {
        operator: 'eq',
        columnValue: (value) => value,
      },
      topLevelAdUnitNames: {
        operator: 'incl',
        columnValue: (value) => value,
      },
      traffickerName: {
        operator: 'incl',
        columnValue: (value) => value,
      },
    }

    if (notDeliveryReport) return seeds

    const filters = Object.keys(seeds)

    return currentItems.reduce(uniquify.valueMap(filters, tracker, valueMap), seeds)
  },
  customFieldFilterValues(state, getters) {
    const { deliveryFilterValues } = getters
    const { customFields } = deliveryFilterValues

    return _.sortAscByCaseInsensitive(customFields, 'value')
  },
  advertiserFilterValues(state, getters) {
    const { deliveryFilterValues } = getters
    const { advertiserName } = deliveryFilterValues

    return _.sortAscByCaseInsensitive(advertiserName, 'value')
  },
  orderFilterValues(state, getters) {
    const { deliveryFilterValues } = getters
    const { orderName } = deliveryFilterValues

    return _.sortAscByCaseInsensitive(orderName, 'value')
  },
  lineItemFilterValues(state, getters) {
    const { deliveryFilterValues } = getters
    const { lineItemName } = deliveryFilterValues

    return _.sortAscByCaseInsensitive(lineItemName, 'value')
  },
  creativeSizesFilterValues(state, getters) {
    const { deliveryFilterValues } = getters
    const { creativeSizes } = deliveryFilterValues

    return _.sortAscByCaseInsensitive(creativeSizes, 'value')
  },
  topLevelAdUnitIdsFilterValues(state, getters) {
    const { deliveryFilterValues } = getters
    const { topLevelAdUnitIds } = deliveryFilterValues

    return _.sortAscByCaseInsensitive(topLevelAdUnitIds, 'value')
  },
  topLevelAdUnitNamesFilterValues(state, getters) {
    const { deliveryFilterValues } = getters
    const { topLevelAdUnitNames } = deliveryFilterValues

    return _.sortAscByCaseInsensitive(topLevelAdUnitNames, 'value')
  },
  priorityFilterValues(state, getters) {
    const { deliveryFilterValues } = getters
    const { priority } = deliveryFilterValues

    return _.sortAscByCaseInsensitive(priority, 'value')
  },
  salespersonNameFilterValues(state, getters) {
    const { deliveryFilterValues } = getters
    const { salespersonName } = deliveryFilterValues

    return _.sortAscByCaseInsensitive(salespersonName, 'value')
  },
  secondaryTraffickerNamesFilterValues(state, getters) {
    const { deliveryFilterValues } = getters
    const { secondaryTraffickerNames } = deliveryFilterValues

    return _.sortAscByCaseInsensitive(secondaryTraffickerNames, 'value')
  },
  statusFilterValues(state, getters) {
    const { deliveryFilterValues } = getters
    const { status } = deliveryFilterValues

    return _.sortAscByCaseInsensitive(status, 'value')
  },
  traffickerNameFilterValues(state, getters) {
    const { deliveryFilterValues } = getters
    const { traffickerName } = deliveryFilterValues

    return _.sortAscByCaseInsensitive(traffickerName, 'value')
  },

  indicatorFilterValues(state) {
    const { indicatorOptions } = state

    return indicatorOptions.map((indicatorOption) => {
      const option = { ...indicatorOption }

      option.value = option.text

      return option
    })
  },

  osiFilterValues(state) {
    const { osiOptions } = state

    return osiOptions.map((osiOption) => {
      const option = { ...osiOption }

      option.value = option.text

      return option
    })
  },

  discrepancyFilterValues(state) {
    const { discrepancyOptions } = state

    return discrepancyOptions.map((discrepancyOption) => {
      const option = { ...discrepancyOption }

      option.value = option.text
      return option
    })
  },

  customAnalyticOptions(state) {
    return state.searchResults.endpoints['custom-analytic']?.items?.list || []
  },
  currentCustomAnalytic(state, getters) {
    const { associationId } = state
    const { currentPageName, customAnalyticOptions } = getters
    const customAnalytic = Array.isArray(customAnalyticOptions) && customAnalyticOptions.find((customAnalyticOption) => {
      const { billingCode, path } = customAnalyticOption
      return billingCode === associationId && path === currentPageName
    })

    return customAnalytic
  },

  getReportItems(state, getters) {
    return (endpointKey) => {
      const { reportPath, associationId, subId } = state
      const { currentPageName, currentViewId, storedItems } = getters
      const dataType = 'ITEMS'
      const needsReportData = currentViewId && associationId && currentPageName === endpointKey && storedItems
      return needsReportData && storage.getPath(dataType, endpointKey, associationId, subId, reportPath)
    }
  },
  clients() {
    const dataType = 'ITEMS'
    const endpointKey = 'client'
    return storage.getAll(dataType, endpointKey) || []
  },
  organizations(state, getters) {
    const dataType = 'ITEMS'
    const endpointKey = PATHS.INTERNAL.ORGANIZATION
    return getters.getReportItems(endpointKey) || storage.getAll(dataType, endpointKey) || []
  },
  persons(state, getters) {
    const dataType = 'ITEMS'
    const endpointKey = PATHS.INTERNAL.PERSON
    return (getters.currentViewId && storage.getAll(dataType, endpointKey)) || []
  },
  projects(state, getters) {
    const dataType = 'ITEMS'
    const endpointKey = PATHS.INTERNAL.PROJECT
    return (getters.currentViewId && storage.getAll(dataType, endpointKey)) || []
  },
  services(state, getters) {
    const dataType = 'ITEMS'
    const endpointKey = 'service'
    return getters.getReportItems(endpointKey) || storage.getAll(dataType, endpointKey) || []
  },
  teams(state, getters) {
    const dataType = 'ITEMS'
    const endpointKey = 'teams'
    return (getters.currentViewId && storage.getAll(dataType, endpointKey)) || []
  },
  times(state, getters) {
    const dataType = 'ITEMS'
    const endpointKey = PATHS.INTERNAL.TIME_SHEET
    return getters.getReportItems(endpointKey) || storage.getAll(dataType, endpointKey) || []
  },
  timeEntries(state, getters) {
    const dataType = 'ITEMS'
    const endpointKey = 'time-entries'
    return getters.getReportItems(endpointKey) || storage.getAll(dataType, endpointKey) || []
  },
  timeTypes(state, getters) {
    const { currentViewId } = getters
    const dataType = 'ITEMS'
    const endpointKey = 'timeType'
    return (currentViewId && storage.getAll(dataType, endpointKey)) || []
  },

  internalTeams(state, getters) {
    const dataType = 'ITEMS'
    const endpointKey = 'internal-team'
    return (getters.currentViewId && storage.getAll(dataType, endpointKey)) || []
  },

  publishers() {
    const dataType = 'ITEMS'
    const endpointKey = 'organization'
    const organizations = storage.getAll(dataType, endpointKey) || []
    return (organizations.items || organizations).filter((org) => org.isPublisher)
  },
  /**
   * PublishersAsTeams seed several endpoints' search options.
   * @returns {Team[]}
   */
  publishersAsTeams(state, getters) {
    const publishersAsTeams = []

    return getters.publishers.reduce((allTeams, publisher) => {
      publisher.teams.forEach((team) => allTeams.push(team))
      return allTeams
    }, publishersAsTeams)
  },
  teamsByReportCode(state, getters) {
    return getters.publishersAsTeams.reduce((teamsByReportCode, team) => {
      teamsByReportCode[team.reportCode] = team
      return teamsByReportCode
    }, {})
  },

  groupedReports(state, getters, rootState) {
    return rootState.report.reports || {}
  },
  globalReports(state, getters) {
    return (getters.groupedReports.global || []).filter((report) => !report.isDeleted)
  },
  sharedReports(state, getters) {
    return (getters.groupedReports.shared || []).filter((report) => !report.isDeleted)
  },
  teamReports(state, getters) {
    return (getters.groupedReports.team || []).filter((report) => !report.isDeleted)
  },
  ownerReports(state, getters) {
    return (getters.groupedReports.owner || []).filter((report) => !report.isDeleted)
  },
  globalTeamReports(state, getters) {
    const { globalReports, teamReports } = getters
    const matchingReports = (globalReport, teamReport) => {
      const { baseReportName, reportPath, routeName } = teamReport

      return globalReport.routeName === routeName
        && globalReport.baseReportName === baseReportName
        && globalReport.reportPath === reportPath
    }

    return teamReports.reduce((uniqueReports, teamReport) => {
      const foundIndex = uniqueReports.findIndex((uniqueReport) => matchingReports(uniqueReport, teamReport))
      if (foundIndex === -1) {
        uniqueReports.push(teamReport)
      } else {
        uniqueReports.splice(foundIndex, 1, teamReport)
      }
      return uniqueReports
    }, globalReports)
  },
  localOwnedAndSharedReports(state, getters) {
    const { sharedReports, ownerReports } = getters
    const seedReports = ownerReports.reduce((seedReport, ownedReport) => {
      seedReport.push(ownedReport)
      return seedReport
    }, [])
    const foundReport = (sharedReport) => seedReports.find((ownedReport) => ownedReport.docId === sharedReport.docId)

    const result = sharedReports.reduce((localOwnedAndSharedReports, sharedReport) => {
      if (!foundReport(sharedReport)) {
        localOwnedAndSharedReports.push(sharedReport)
      }

      return localOwnedAndSharedReports
    }, seedReports)
    return result
  },

  currentAssociationIds(state, getters, rootState, rootGetters) {
    const { associationId, subId } = state
    const isAssociationIdUndefinedOrAll = !associationId || associationId === 'all-associations'
    const isSubIdUndefinedOrAll = !subId || subId === 'all-secondary'

    const defaultAssociationId = rootGetters['auth/defaultAssociationId']
    const defaultSubId = rootGetters['auth/defaultSubId']

    const newAssociationId = isAssociationIdUndefinedOrAll ? defaultAssociationId : associationId
    const newSubId = isSubIdUndefinedOrAll ? defaultSubId : subId

    return { newAssociationId, newSubId }
  },
  associationIdAndSubId(state) {
    const { associationId, query, selectedIds, subId } = state
    const hasQueryDefined = () => query && query.associationId && { associationId: query.associationId, subId: query.subId }
    const hasAssociationId = () => associationId && { associationId, subId: subId || '-1' }
    const hasSelectedIds = () => selectedIds && selectedIds.associationId && selectedIds

    return hasSelectedIds() || hasQueryDefined() || hasAssociationId() || {}
  },

  selectedReportTeam(state, getters) {
    const { associationIdAndSubId, currentNav } = getters
    const { associationId, subId } = associationIdAndSubId
    const getAllAssociations = () => (currentNav && associationId === 'all-associations' && subId === 'all-secondary' && {
      baseReportName: currentNav.baseReportName,
      billingCode: 'all-associations',
      teamName: 'All Associations',
      routeName: currentNav.routeName,
      teamId: 'all-secondary',
    })

    return getAllAssociations() || (associationId && getters.publishersAsTeams.find((team) => team.billingCode === associationId && team.teamId === subId)) || null
  },

  findIndexOfConflictingReport() {
    const hasMatchingDocId = (proposedReport, existingReport) => existingReport.docId === proposedReport.docId
    const hasMatchingRouteNameAndBaseReportName = (proposedReport, existingReport) => existingReport.routeName === proposedReport.routeName
      && existingReport.baseReportName === proposedReport.baseReportName
    const hasMatchingAssociationId = (proposedReport, existingReport) => existingReport.associationId === proposedReport.associationId
    const hasMatchingSubId = (proposedReport, existingReport) => existingReport.subId === proposedReport.subId
    const hasMatchingAssociationIdAndSubId = (proposedReport, existingReport) => hasMatchingAssociationId(proposedReport, existingReport)
      && hasMatchingSubId(proposedReport, existingReport)

    const hasAllAssociations = (proposedReport, existingReport) => proposedReport.associationId === 'all-associations' || existingReport.associationId === 'all-associations'
    const hasMatchingAssociations = (proposedReport, existingReport) => hasAllAssociations(proposedReport, existingReport)
      || hasMatchingAssociationIdAndSubId(proposedReport, existingReport)

    const hasMatchingReportPath = (proposedReport, existingReport) => existingReport.reportPath === proposedReport.reportPath
    const hasMatchingRoute = (proposedReport, existingReport) => hasMatchingRouteNameAndBaseReportName(proposedReport, existingReport)
      && hasMatchingAssociations(proposedReport, existingReport)
      && hasMatchingReportPath(proposedReport, existingReport)

    const findConflictingReport = (proposedReport, existingReport) => hasMatchingDocId(proposedReport, existingReport)
      || hasMatchingRoute(proposedReport, existingReport)

    return (newReport, existingReports) => existingReports.findIndex((existingReport) => findConflictingReport(newReport, existingReport))
  },
  prepareReport(state, getters, rootState, rootGetters) {
    // eslint-disable-next-line complexity
    return (report, newProperties) => {
      const newReport = createObject(report, newProperties)
      const { associationId, baseReportName, isTemporary, reportPath, routeName, subId } = newReport
      const action = 'read'
      const { origin } = window.location
      const userEmail = rootGetters['auth/userEmail']
      const inDevMode = rootGetters['auth/inDevMode']
      const isSelfInternal = rootGetters['auth/isSelfInternal']
      const foundRole = report.roles && report.roles.find((role) => role.userId === userEmail)

      const isOwner = report.ownerId === userEmail
      const isEditor = foundRole && foundRole.role === 'editor'
      const isViewer = foundRole && foundRole.role === 'viewer'

      const isGlobalOrTeamReport = ['global', 'team'].indexOf(report.groupKey) !== -1
      const isSharedReport = report.groupKey === 'shared'
      const isTemplate = isGlobalOrTeamReport && report.defaultRole === 'viewer'
      const isProtected = isTemplate || (isSharedReport && (!foundRole || foundRole.role === 'viewer'))
      const isStarterReport = isGlobalOrTeamReport && report.defaultRole === 'editor'

      const isInternalEditor = isSelfInternal && !isProtected

      newReport.href = `/${routeName}/${baseReportName}/?action=${action}&associationId=${associationId}&reportPath=${reportPath}&subId=${subId || '-1'}`
      newReport.hrefShare = `${origin}/${routeName}/${baseReportName}/?action=${action}&associationId=${associationId}&reportPath=${reportPath}&subId=${subId || '-1'}&share=true`

      newReport.isEditor = isEditor
      newReport.isOwner = isOwner
      newReport.isViewer = isViewer

      newReport.isEditable = inDevMode || isOwner || isEditor || isTemporary || false
      newReport.isProtected = isProtected || false
      newReport.isSharedReport = isSharedReport
      newReport.isStarterReport = isStarterReport || false
      newReport.isTemplate = isTemplate || false

      newReport.canShareHard = inDevMode || isOwner || isEditor || false
      newReport.canShareSoft = inDevMode || isOwner || isEditor || isViewer || false

      newReport.canDeleteHard = inDevMode || isOwner || isTemporary || false
      newReport.canDeleteSoft = inDevMode || isOwner || isEditor || isTemporary || false

      newReport.canEditOwner = inDevMode || isInternalEditor || false
      newReport.canEditScope = inDevMode || isInternalEditor || isOwner || isTemporary || false
      newReport.canEditRole = inDevMode || isInternalEditor || isOwner || isEditor || isTemporary || false

      newReport.canEditReportPath = inDevMode || false
      newReport.canEditIsFavorite = inDevMode || isOwner || isTemporary || false
      newReport.canEditIsMetric = inDevMode || isOwner || isTemporary || false
      newReport.canEditIsArchived = inDevMode || isOwner || isEditor || false

      return newReport
    }
  },
  /**
   * Returns a function that maps dynamic values onto the given reports.
   * @returns {function(Object[], Object): Object[]} A function that returns the updated queries.
   */
  prepareReports(state, getters) {
    return (reportsToPrepare, newProperties) => reportsToPrepare.map((report) => getters.prepareReport(report, newProperties))
  },
  /**
   * Compares global and local reports, merging and overwriting as needed.
   * @param state Vuex state
   * @param getters Vuex getters
   * @returns {Array} An array of unique global and local reports.
   */
  uniqueReports(state, getters) {
    const { globalTeamReports, localOwnedAndSharedReports } = getters
    const addReport = (allUniqueReports, globalReport) => {
      allUniqueReports.push(globalReport)
    }
    const addReportAtIndex = (allUniqueReports, globalReport, foundUniqueReportIndex) => {
      // Add global but do not delete local. We'll need both for a later merge.
      allUniqueReports.splice(foundUniqueReportIndex, 0, globalReport)
    }
    /**
     * Whether to merge a report into an existing one. If so, returns the index of the report to merge into.
     * @returns {(boolean|number)}
     */
    const requireMerge = (allUniqueReports, globalReport) => {
      const index = getters.findIndexOfConflictingReport(globalReport, allUniqueReports)
      return index !== -1 && index
    }
    const overwriteReport = (foundUniqueReportIndex, allUniqueReports, globalReport) => {
      allUniqueReports.splice(foundUniqueReportIndex, 1, globalReport)
    }
    const handleGlobalReports = (foundUniqueReportIndex, allUniqueReports, globalReport, foundReport) => {
      if (!foundReport.isClientEnforced && globalReport.isClientEnforced) {
        // Enforce the proposed report by overwriting the found report.
        overwriteReport(foundUniqueReportIndex, allUniqueReports, globalReport)
      } else if (foundReport.isClientEnforced && !globalReport.isClientEnforced) {
        // Do not insert the proposed report.
      } else {
        // The last global report in the array is the most recent, so it replaces the previous one.
        overwriteReport(foundUniqueReportIndex, allUniqueReports, globalReport)
      }
    }
    const mergeReports = (foundUniqueReportIndex, allUniqueReports, globalReport) => {
      const foundReport = allUniqueReports[foundUniqueReportIndex]
      const foundReportIsLocal = foundReport.scope === 'local'

      if (foundReportIsLocal) {
        if (globalReport.isClientEnforced) {
          // The global report is enforced and not editable, so we overwrite the local report.
          overwriteReport(foundUniqueReportIndex, allUniqueReports, globalReport)
        } else {
          // Add global but do not delete local. We'll need both for a later merge.
          addReportAtIndex(allUniqueReports, globalReport, foundUniqueReportIndex)
        }
      } else {
        // Both reports are global, so we need to determine which one to keep.
        handleGlobalReports(foundUniqueReportIndex, allUniqueReports, globalReport, foundReport)
      }
    }

    const seedUniqueReports = localOwnedAndSharedReports.map((object) => copyObject(object))

    return globalTeamReports
      .reduce((allUniqueReports, globalReport) => {
        const foundUniqueReportIndex = requireMerge(allUniqueReports, globalReport)

        if (foundUniqueReportIndex === false) {
          addReport(allUniqueReports, globalReport)
        } else {
          mergeReports(foundUniqueReportIndex, allUniqueReports, globalReport)
        }

        return allUniqueReports
      }, seedUniqueReports)
      .sort((a, b) => a.index - b.index)
  },
  preparedReports(state, getters) {
    const { uniqueReports, prepareReports } = getters
    return prepareReports(uniqueReports)
  },
  uniqueReportOptions(state, getters) {
    const { temporaryReportOptions } = state
    const getUniqueReports = () => {
      const seedUniqueReports = getters.preparedReports.map((object) => copyObject(object))
      return temporaryReportOptions.reduce((uniqueReports, proposedReport) => {
        const indexOfConflictingReport = getters.findIndexOfConflictingReport(proposedReport, uniqueReports)

        if (indexOfConflictingReport === -1) {
          uniqueReports.push(proposedReport)
        }

        return uniqueReports
      }, seedUniqueReports)
    }
    const uniqueReportOptions = temporaryReportOptions.length ? getUniqueReports() : getters.preparedReports

    return uniqueReportOptions.sort((a, b) => a.index - b.index)
  },

  makeLocalReportFromGlobalReport(state, getters, rootState, rootGetters) {
    const userEmail = rootGetters['auth/userEmail']
    const { prepareReport } = getters

    return (report, reportIndexInArray, selectedReportTeam) => {
      const { index: reportIndex, isStarterReport, reportPath } = report
      const docId = createReportId()
      const scope = isStarterReport ? 'local' : report.scope || 'local'
      const ownerId = isStarterReport ? userEmail : report.ownerId || userEmail
      const defaultRole = report.defaultRole || 'viewer'
      const newRoleProperties = {
        isFavorite: report.isFavorite || false,
        isMetric: report.isMetric || false,
        role: defaultRole,
      }
      const foundRole = report.roles && report.roles.find((role) => role.userId === userEmail)
      const addNewRole = (parentObject) => {
        const newRole = createReportRole(newRoleProperties, userEmail)
        return pushRole(parentObject, newRole)
      }
      // @TODO Use .map to standardize roles if foundRole.
      const standardizeRoles = () => (foundRole ? report.roles : addNewRole(report))
      const roles = report.roles && report.roles.length ? standardizeRoles() : addNewRole(report)

      const standardizeShares = () => roles.reduce((standardizedShares, role) => {
        const shouldBeInShare = role.role !== 'owner' && !role.isRevoked && !role.isDeleted

        if (shouldBeInShare && standardizedShares.indexOf(role.userId) === -1) {
          standardizedShares.push(role.userId)
        }

        return standardizedShares
      }, report.shares)

      const shares = roles && roles.length ? standardizeShares() : []

      const newProperties = {
        associationId: selectedReportTeam.associationId,
        defaultRole,
        docId,
        docIdOnParent: report.docId,
        index: typeof reportIndex === 'number' ? reportIndex : reportIndexInArray,
        isArchived: report.isArchived || false,
        isClientEnforced: report.isClientEnforced || false,
        isFavorite: report.isFavorite || false,
        isMetric: report.isMetric || false,
        ownerId,
        reportPath,
        roles,
        scope,
        shares,
        subId: selectedReportTeam.subId,
      }

      // @NOTE Draggable requires this not be a frozen object, allowing the user to update the index.
      return prepareReport(report, newProperties)
    }
  },
  makeReportOptionsForTeam(state, getters) {
    const { prepareReport } = getters
    return (selectedReportTeam, currentReportsForBaseReport) => currentReportsForBaseReport.reduce((newReports, proposedReport) => {
      const isSelectedOrganizationAnAllAssociation = selectedReportTeam && selectedReportTeam.billingCode === 'all-associations' && selectedReportTeam.teamId === 'all-secondary'
      const haveSelectedAnOrganization = !isSelectedOrganizationAnAllAssociation
      const isTeamReport = () => proposedReport.associationId === selectedReportTeam.billingCode && proposedReport.subId === String(selectedReportTeam.teamId)
      const isReportForAllAssociations = () => haveSelectedAnOrganization && proposedReport.scope === 'global' && proposedReport.associationId === 'all-associations' && proposedReport.subId === 'all-secondary'

      const indexOfConflictingReport = getters.findIndexOfConflictingReport(proposedReport, newReports)
      const isUniqueReport = indexOfConflictingReport === -1
      const updateReportsArray = (newReport) => {
        if (isUniqueReport) {
          newReports.push(newReport)
        } else {
          newReports.splice(indexOfConflictingReport, 1, newReport)
        }
      }
      const getIndex = () => (isUniqueReport ? newReports.length : indexOfConflictingReport)
      const index = typeof proposedReport.index === 'number' ? proposedReport.index : getIndex()

      if (isReportForAllAssociations()) {
        const newReport = getters.makeLocalReportFromGlobalReport(proposedReport, index, selectedReportTeam)

        updateReportsArray(newReport)
      } else if (isTeamReport()) {
        const newProperties = { index }
        const newReport = prepareReport(proposedReport, newProperties)

        updateReportsArray(newReport)
      }

      return newReports.sort((a, b) => a.index - b.index)
    }, [])
  },
  reportOptionsForSelectedTeam(state, getters, rootState) {
    const { uniqueReportOptions, selectedReportTeam } = getters
    const { baseReportName, routeName } = state
    const { showArchivedReports } = rootState.report
    const filterByRoute = (report) => report.routeName === routeName && report.baseReportName === baseReportName
    const filterByRouteAndArchiveStatus = (report) => filterByRoute(report) && (showArchivedReports || !report.isArchived)
    const currentReportsForBaseReport = uniqueReportOptions.filter(filterByRouteAndArchiveStatus)

    return (selectedReportTeam && getters.makeReportOptionsForTeam(selectedReportTeam, currentReportsForBaseReport)) || []
  },

  reportOptionsByReportCode(state, getters) {
    const { searchResults } = state
    const { publishersAsTeams } = getters
    const { endpoints } = searchResults
    const endpointKeys = Object.keys(endpoints)
    const excludeEndpoints = ['dashboard']
    const allAssociationsAsTeams = {
      associationId: 'all-associations',
      reportCode: 'all-associations/all-secondary',
      subId: 'all-secondary',
    }
    const associationsAsTeams = [allAssociationsAsTeams, ...publishersAsTeams]

    return associationsAsTeams.reduce((reportOptionsByReportCode, associationTeam) => {
      reportOptionsByReportCode[associationTeam.reportCode] = endpointKeys.reduce((reportOptionsForEndpoint, endpointKey) => {
        if (excludeEndpoints.indexOf(endpointKey) === -1) {
          const endpoint = endpoints[endpointKey]
          const { itemsArray } = endpoint
          const { associationId, subId } = associationTeam
          const publisherResults = itemsArray && itemsArray.filter((item) => item.associationId === associationId && item.subId === subId)

          if (publisherResults) {
            reportOptionsForEndpoint.push(...publisherResults)
          }
        }

        return reportOptionsForEndpoint
      }, [])

      return reportOptionsByReportCode
    }, {})
  },

  notFavorites(state, getters) {
    return getters.reportOptionsForSelectedTeam.filter((option) => !option.isFavorite).map((report) => copyObject(report))
  },

  personFilterValues(state, getters) {
    const { isInternalRoute, persons } = getters

    const seeds = {
      billingCodes: [],
      email: [],
      hasAccessErrors: [],
      networkCodes: [],
      orgIds: [],
      orgNames: [],
      personCode: [],
      personName: [],
      level: [],
      role: [],
      teamCodes: [],
      teamIds: [],
      teamNames: [],
    }
    const tracker = {
      billingCodes: [],
      email: [],
      hasAccessErrors: [],
      networkCodes: [],
      orgIds: [],
      orgNames: [],
      personCode: [],
      personName: [],
      level: [],
      role: [],
      teamCodes: [],
      teamIds: [],
      teamNames: [],
    }
    const valueMap = {
      billingCodes: {
        operator: 'incl',
        columnValue: (value) => value,
      },
      email: {
        operator: 'incl',
        columnValue: (value) => value,
      },
      hasAccessErrors: {
        operator: 'incl',
        columnValue: (value) => value,
      },
      networkCodes: {
        operator: 'incl',
        columnValue: (value) => value,
      },
      orgIds: {
        operator: 'incl',
        columnValue: (value) => value,
      },
      orgNames: {
        operator: 'incl',
        columnValue: (value) => value,
      },
      personCode: {
        operator: 'eq',
        columnValue: (value) => value,
      },
      personName: {
        operator: 'incl',
        columnValue: (value) => value,
      },
      level: {
        operator: 'incl',
        columnValue: (value, item) => item.access?.level,
      },
      role: {
        operator: 'incl',
        columnValue: (value, item) => item.access?.role,
      },
      teamCodes: {
        operator: 'incl',
        columnValue: (value) => value,
      },
      teamIds: {
        operator: 'incl',
        columnValue: (value) => value,
      },
      teamNames: {
        operator: 'incl',
        columnValue: (value) => value,
      },
    }

    if (!isInternalRoute) return seeds

    const filters = Object.keys(seeds)

    return persons.reduce(uniquify.valueMap(filters, tracker, valueMap), seeds)
  },
  projectFilterValues(state, getters) {
    const { currentItems, currentPageName } = getters
    const notProjectRoute = !['project'].includes(currentPageName)

    const seeds = {
      projectCode: [],
      projectName: [],
    }
    const tracker = {
      projectCode: [],
      projectName: [],
    }
    const valueMap = {
      projectCode: {
        operator: 'eq',
        columnValue: (value) => value,
      },
      projectName: {
        operator: 'incl',
        columnValue: (value) => value,
      },
    }

    if (notProjectRoute) return seeds

    const filters = Object.keys(seeds)

    return currentItems.reduce(uniquify.valueMap(filters, tracker, valueMap), seeds)
  },
  timeFilterValues(state, getters) {
    const { currentItems, currentPageName } = getters
    const notTimeReport = ![PATHS.INTERNAL.TIME_ROLLUP, PATHS.INTERNAL.TIME_SHEET].includes(currentPageName)

    const seeds = {
      billingCode: [],
      clientName: [],
      projectId: [],
      projectName: [],
      timeClassificationName: [],
      workNote: [],
    }
    const tracker = {
      billingCode: [],
      clientName: [],
      projectId: [],
      projectName: [],
      timeClassificationName: [],
      workNote: [],
    }
    const valueMap = {
      billingCode: {
        operator: 'incl',
        columnValue: (value) => value,
      },
      clientName: {
        operator: 'incl',
        columnValue: (value) => value,
      },
      projectId: {
        operator: 'eq',
        columnValue: (value) => value,
      },
      projectName: {
        operator: 'incl',
        columnValue: (value) => value,
      },
      timeClassificationName: {
        operator: 'eq',
        columnValue: (value) => value,
      },
      workNote: {
        operator: 'incl',
        columnValue: (value) => value,
      },
    }

    if (notTimeReport) return seeds

    const filters = Object.keys(seeds)

    return currentItems.reduce(uniquify.valueMap(filters, tracker, valueMap), seeds)
  },

  orgFilterValues(state, getters) {
    const { isInternalRoute, organizations } = getters

    const seeds = {
      billingCode: [],
      hasHealthAdxReport: [],
      hasHealthNetworkReport: [],
      hasDiscrepancyReport: [],
      hasYieldPartnerReport: [],
      hasPacingReport: [],
      hasTimesheetReport: [],
      isInternalUseOnly: [],
      networkCode: [],
      orgId: [],
      orgName: [],
    }
    const tracker = {
      billingCode: [],
      hasHealthAdxReport: [],
      hasHealthNetworkReport: [],
      hasDiscrepancyReport: [],
      hasYieldPartnerReport: [],
      hasPacingReport: [],
      hasTimesheetReport: [],
      isInternalUseOnly: [],
      networkCode: [],
      orgId: [],
      orgName: [],
    }
    const valueMap = {
      billingCode: {
        operator: 'incl',
        columnValue: (value) => value,
      },
      hasHealthAdxReport: {
        operator: 'is',
        columnValue: (value) => value,
      },
      hasHealthNetworkReport: {
        operator: 'is',
        columnValue: (value) => value,
      },
      hasDiscrepancyReport: {
        operator: 'is',
        columnValue: (value) => value,
      },
      hasYieldPartnerReport: {
        operator: 'is',
        columnValue: (value) => value,
      },
      hasPacingReport: {
        operator: 'is',
        columnValue: (value) => value,
      },
      hasTimesheetReport: {
        operator: 'is',
        columnValue: (value) => value,
      },
      isInternalUseOnly: {
        operator: 'is',
        columnValue: (value) => value,
      },
      networkCode: {
        operator: 'eq',
        columnValue: (value) => value,
      },
      orgId: {
        operator: 'incl',
        columnValue: (value) => value,
      },
      orgName: {
        operator: 'incl',
        columnValue: (value) => value,
      },
    }

    if (!isInternalRoute) return seeds

    const filters = Object.keys(seeds)

    return organizations.reduce(uniquify.valueMap(filters, tracker, valueMap), seeds)
  },
  billingCodeFilterValues(state, getters) {
    const { currentPageName, orgFilterValues, timeFilterValues } = getters
    const isOrgRoute = ['organization'].includes(currentPageName)
    const { billingCode } = isOrgRoute ? orgFilterValues : timeFilterValues

    return _.sortAscByCaseInsensitive(billingCode, 'value')
  },
  networkCodeFilterValues(state, getters) {
    const { orgFilterValues } = getters
    const { networkCode } = orgFilterValues

    return _.sortAscByCaseInsensitive(networkCode, 'value')
  },
  orgIdFilterValues(state, getters) {
    const { orgFilterValues } = getters
    const { orgId } = orgFilterValues

    return _.sortAscByCaseInsensitive(orgId, 'value')
  },
  orgNameFilterValues(state, getters) {
    const { orgFilterValues } = getters
    const { orgName } = orgFilterValues

    return _.sortAscByCaseInsensitive(orgName, 'value')
  },

  billingCodesFilterValues(state, getters) {
    const { personFilterValues } = getters
    const { billingCodes } = personFilterValues

    return _.sortAscByCaseInsensitive(billingCodes, 'value')
  },
  networkCodesFilterValues(state, getters) {
    const { personFilterValues } = getters
    const { networkCodes } = personFilterValues

    return _.sortAscByCaseInsensitive(networkCodes, 'value')
  },
  orgIdsFilterValues(state, getters) {
    const { personFilterValues } = getters
    const { orgIds } = personFilterValues

    return _.sortAscByCaseInsensitive(orgIds, 'value')
  },
  orgNamesFilterValues(state, getters) {
    const { personFilterValues } = getters
    const { orgNames } = personFilterValues

    return _.sortAscByCaseInsensitive(orgNames, 'value')
  },

  teamFilterValues(state, getters) {
    const { isInternalRoute, teams } = getters

    const seeds = {
      teamCode: [],
      teamId: [],
      teamName: [],
    }
    const tracker = {
      teamCode: [],
      teamId: [],
      teamName: [],
    }
    const valueMap = {
      teamCode: {
        operator: 'incl',
        columnValue: (value) => value,
      },
      teamId: {
        operator: 'incl',
        columnValue: (value) => value,
      },
      teamName: {
        operator: 'incl',
        columnValue: (value) => value,
      },
    }

    if (!isInternalRoute) return seeds

    const filters = Object.keys(seeds)

    return teams.reduce(uniquify.valueMap(filters, tracker, valueMap), seeds)
  },
  teamCodeFilterValues(state, getters) {
    const { teamFilterValues } = getters
    const { teamCode } = teamFilterValues

    return _.sortAscByCaseInsensitive(teamCode, 'value')
  },
  teamIdFilterValues(state, getters) {
    const { teamFilterValues } = getters
    const { teamId } = teamFilterValues

    return _.sortAscByCaseInsensitive(teamId, 'value')
  },
  teamNameFilterValues(state, getters) {
    const { teamFilterValues } = getters
    const { teamName } = teamFilterValues

    return _.sortAscByCaseInsensitive(teamName, 'value')
  },

  teamCodesFilterValues(state, getters) {
    const { personFilterValues } = getters
    const { teamCodes } = personFilterValues

    return _.sortAscByCaseInsensitive(teamCodes, 'value')
  },
  teamIdsFilterValues(state, getters) {
    const { personFilterValues } = getters
    const { teamIds } = personFilterValues

    return _.sortAscByCaseInsensitive(teamIds, 'value')
  },
  teamNamesFilterValues(state, getters) {
    const { personFilterValues } = getters
    const { teamNames } = personFilterValues

    return _.sortAscByCaseInsensitive(teamNames, 'value')
  },

  hasDiscrepancyReportFilterValues(state, getters) {
    const { orgFilterValues } = getters
    const { hasDiscrepancyReport } = orgFilterValues

    return _.sortAscByCaseInsensitive(hasDiscrepancyReport, 'value')
  },
  hasPacingReportFilterValues(state, getters) {
    const { orgFilterValues } = getters
    const { hasPacingReport } = orgFilterValues

    return _.sortAscByCaseInsensitive(hasPacingReport, 'value')
  },
  hasTimesheetReportFilterValues(state, getters) {
    const { orgFilterValues } = getters
    const { hasTimesheetReport } = orgFilterValues

    return _.sortAscByCaseInsensitive(hasTimesheetReport, 'value')
  },
  isInternalUseOnlyFilterValues(state, getters) {
    const { orgFilterValues } = getters
    const { isInternalUseOnly } = orgFilterValues

    return _.sortAscByCaseInsensitive(isInternalUseOnly, 'value')
  },
  workNoteFilterValues(state, getters) {
    const { timeFilterValues } = getters
    const { workNote } = timeFilterValues

    return _.sortAscByCaseInsensitive(workNote, 'value')
  },

  emailFilterValues(state, getters) {
    const { personFilterValues } = getters
    const { email } = personFilterValues

    return _.sortAscByCaseInsensitive(email, 'value')
  },
  hasAccessErrorsFilterValues(state, getters) {
    const { personFilterValues } = getters
    const { hasAccessErrors } = personFilterValues

    return _.sortAscByCaseInsensitive(hasAccessErrors, 'value')
  },
  levelFilterValues(state, getters) {
    const { personFilterValues } = getters
    const { level } = personFilterValues

    return _.sortAscByCaseInsensitive(level, 'value')
  },
  roleFilterValues(state, getters) {
    const { personFilterValues } = getters
    const { role } = personFilterValues

    return _.sortAscByCaseInsensitive(role, 'value')
  },

  projectNameFilterValues(state, getters) {
    const { currentPageName, projectFilterValues, timeFilterValues } = getters
    const isTimeRoute = [PATHS.INTERNAL.TIME_ROLLUP, PATHS.INTERNAL.TIME_SHEET].includes(currentPageName)
    const { projectName } = isTimeRoute ? timeFilterValues : projectFilterValues

    return _.sortAscByCaseInsensitive(projectName, 'value')
  },

  timeTypeFilterValues(state, getters) {
    const { timeFilterValues } = getters
    const { timeClassificationName } = timeFilterValues

    return _.sortAscByCaseInsensitive(timeClassificationName, 'value')
  },

  keyField(state, getters) {
    const { keyFields } = state
    const { currentPageName } = getters

    return keyFields[currentPageName]
  },
  sizeField(state, getters) {
    const { sizeFields } = state
    const { currentPageName } = getters

    return sizeFields[currentPageName]
  },

  isAdManagerRoute(state, getters) {
    const { admanagerRoutes } = state
    const { currentPageName } = getters
    return admanagerRoutes.indexOf(currentPageName) !== -1
  },
  isDashboardRoute(state, getters) {
    const { dashboardRoutes } = state
    const { currentPageName } = getters
    // We excluded Home from isDashboardRoute because Home is an exception to report rendering rules.
    return dashboardRoutes.indexOf(currentPageName) !== -1 && currentPageName !== 'home'
  },
  isDeliveryRoute(state, getters) {
    const { deliveryRoutes } = state
    const { currentPageName } = getters
    return deliveryRoutes.indexOf(currentPageName) !== -1
  },
  isDevRoute(state, getters) {
    const { devRoutes } = state
    const { currentPageName } = getters
    return devRoutes.indexOf(currentPageName) !== -1
  },
  isHomeRoute(state, getters) {
    const { currentPageName } = getters
    return currentPageName === 'home'
  },
  isInternalRoute(state, getters) {
    const { internalRoutes } = state
    const { currentPageName } = getters
    return internalRoutes.indexOf(currentPageName) !== -1
  },
  isProgrammaticRoute(state, getters) {
    const { programmaticRoutes } = state
    const { currentPageName } = getters
    return programmaticRoutes.indexOf(currentPageName) !== -1
  },
  isCustomAnalyticRoute(state, getters) {
    return getters.currentRouteName === 'custom-analytics' // || customAnalyticRoutes.indexOf(getters.currentPageName) !== -1
  },
  isDocsRoute(state) {
    return state.routeName === 'docs'
  },
  isReportRoute(state, getters) {
    return !getters.isDocsRoute && !getters.isDevRoute
  },
  isAssociationIdRequiredOnRoute(state, getters) {
    return getters.isDashboardRoute || getters.isReportRoute
  },
  isTimeRoute(state, getters) {
    return [PATHS.INTERNAL.TIME_ROLLUP, PATHS.INTERNAL.TIME_SHEET].includes(getters.currentPageName)
  },

  navigationByKey(state) {
    const { navigation } = state
    return navigation && navigation.reduce((navigationByKey, navItem) => {
      const { baseReportName, routeName } = navItem
      navigationByKey[baseReportName || routeName] = navItem
      return navigationByKey
    }, {})
  },
  currentNav(state, getters) {
    return getters.navigationByKey[getters.currentPageName]
  },
  allowedNavigationEndpoints(state, getters) {
    return Object.keys(getters.navigationByKey)
  },
  reportOptions(state, getters) {
    const { navigationByKey } = getters

    return getters.allowedNavigationEndpoints.map((navKey) => ({ text: navigationByKey[navKey]?.title || 'Unknown', value: navKey }))
  },

  networkTeams(state, getters, rootState, rootGetters) {
    const networks = rootGetters['auth/networkPermissions']
    const { teamsByReportCode } = getters
    const networkTeams = []
    const getTeamByReportCode = (billingCode, teamId) => teamsByReportCode[`${billingCode}/${teamId}`]

    if (networks) {
      networks.forEach((network) => {
        const { organization, team: teams } = network
        const { code: billingCode } = organization || {}
        const isValidTeam = Array.isArray(teams) && !teams.find((team) => typeof team !== 'object')

        if (isValidTeam) {
          teams.forEach((team) => {
            const foundTeam = getTeamByReportCode(billingCode, team.id)
            if (foundTeam) networkTeams.push(foundTeam)
          })
        }
      })
    }

    return networkTeams
  },

  makeSearchHref() {
    return (reportPath, navigationItem, searchOption) => navigationItem && navigationItem.href(searchOption.associationId, searchOption.subId, reportPath)
  },
  makeSearchLabel() {
    return (navigationItem, searchOption) => navigationItem && `${navigationItem.title}: ${searchOption.text}`
  },
  makeBookmark(state, getters) {
    return (foundNavigationItem, foundSearchOption, index) => {
      const { reportPath } = foundSearchOption
      const href = getters.makeSearchHref(reportPath, foundNavigationItem, foundSearchOption)
      const reportLabel = getters.makeSearchLabel(foundNavigationItem, foundSearchOption)

      return {
        associationId: foundSearchOption.associationId,
        baseReportName: foundSearchOption.baseReportName,
        href,
        index,
        reportCode: foundSearchOption.reportCode,
        reportLabel,
        reportPath,
        routeName: foundSearchOption.routeName,
        subId: foundSearchOption.subId,
      }
    }
  },
  mapBookmarkToSearchOption(state, getters) {
    const { accessFields, searchResults } = state
    const { navigationByKey } = getters

    return (bookmark) => {
      const {
        associationId,
        baseReportName,
        index: bookmarkIndex,
        reportPath,
        routeName,
        subId,
      } = bookmark
      const navKey = baseReportName || routeName

      const searchOptions = getters.getSearchOptions(navKey, searchResults)
      const foundSearchOption = searchOptions && searchOptions.find((option) => option.associationId === associationId
        && option.baseReportName === baseReportName
        && option.routeName === routeName
        && option.subId === subId
        && option.reportPath === reportPath)

      // Uses accessFields to prevent creating a bookmark irrelevant to the team.
      const hasAccessProperty = accessFields[navKey]
      const hasReport = hasAccessProperty && foundSearchOption ? foundSearchOption[hasAccessProperty] : true
      const foundNavigationItem = hasReport && navigationByKey[navKey]

      if (foundNavigationItem && foundSearchOption) {
        return getters.makeBookmark(foundNavigationItem, foundSearchOption, bookmarkIndex)
      }

      return false
    }
  },
  getBookmarksAcrossTeams(state, getters) {
    const { reportPathDefault, searchResults } = state
    const { navigationByKey } = getters
    const { endpoints: searchEndpoints } = searchResults || {}
    const searchEndpointKeys = searchEndpoints && typeof searchEndpoints === 'object' && Object.keys(searchEndpoints)
    const navEndpointKeys = navigationByKey && typeof navigationByKey === 'object' && Object.keys(navigationByKey)
    const haveNavigationByKeys = navEndpointKeys && navEndpointKeys.length
    const canMakeBookmarks = haveNavigationByKeys && searchEndpointKeys

    return (teams) => canMakeBookmarks && teams.reduce((allTeamReports, team) => {
      searchEndpointKeys.forEach((searchEndpointKey) => {
        const searchEndpoint = searchEndpoints[searchEndpointKey]
        const newBookmark = getters.mapBookmarkToSearchOption({
          associationId: team.associationId,
          baseReportName: searchEndpoint.baseReportName,
          reportPath: reportPathDefault,
          routeName: searchEndpoint.routeName,
          subId: team.subId,
        })

        if (newBookmark) {
          allTeamReports.push(newBookmark)
        }
      })

      return allTeamReports
    }, [])
  },
  defaultBookmarks(state, getters) {
    return getters.getBookmarksAcrossTeams(getters.networkTeams) || []
  },
  bookmarks(state, getters, rootState, rootGetters) {
    const bookmarks = rootGetters['auth/bookmarks'] || []
    const hasBookmarks = Array.isArray(bookmarks) && bookmarks.length
    const getBookmarks = () => bookmarks.reduce((validBookmarks, bookmark) => {
      const newBookmark = getters.mapBookmarkToSearchOption(bookmark)
      if (newBookmark) validBookmarks.push(newBookmark)
      return validBookmarks
    }, [])

    return (hasBookmarks && getBookmarks()) || getters.defaultBookmarks
  },

  useNetworkSettings(state, getters, rootState, rootGetters) {
    const inDemoMode = rootGetters['auth/inDemoMode']
    const networkSettings = rootGetters['auth/networkSettings']
    return !inDemoMode && networkSettings.length
  },
  networkSettings(state, getters, rootState, rootGetters) {
    return getters.useNetworkSettings ? rootGetters['auth/networkSettings'] : getters.networkTeams
  },
  reduceNetwork(state, getters) {
    return (searchOptions, baseReportName, reportPath, routeName) => (networks, team, index) => {
      const { associationId, subId } = team
      const foundSearchOption = getters.findSearchOption(searchOptions, associationId, baseReportName, reportPath, routeName, subId)

      if (foundSearchOption) {
        const { href, text: reportLabel } = foundSearchOption
        networks.push({ associationId, baseReportName, href, index, reportLabel, reportPath, routeName, subId })
      }

      return networks
    }
  },
  networks(state, getters, rootState, rootGetters) {
    const { reportPathDefault: reportPath, searchResults } = state
    const { navigationByKey } = getters
    const networks = rootGetters['auth/networks']
    const currentNav = navigationByKey[PATHS.DASHBOARD.BASE]
    const { baseReportName, routeName } = currentNav || {}
    const navKey = baseReportName || routeName
    const searchOptions = navKey && getters.getSearchOptions(navKey, searchResults)
    const reducer = getters.reduceNetwork(searchOptions, baseReportName, reportPath, routeName)

    const success = () => networks.reduce((dashboards, setting) => {
      reducer(dashboards, setting, setting.index)
      return dashboards
    }, [])
    const error = () => (!searchOptions || !searchOptions.length) && []

    return error() || success()
  },

  contacts(state, getters, rootState, rootGetters) {
    const { internalTeams, persons } = getters
    const currentUserManagerPersonId = rootGetters['auth/managerPersonId']
    const currentUserPersonId = rootGetters['auth/personId']
    const relationshipMap = {
      isManagedByCurrentUser: () => 'Delegate',
      isManagerOfCurrentUser: () => 'Manager',
      shareManager: () => 'Coworker',
      shareTeamId: () => 'Team mate',
    }

    return persons.reduce((contacts, person) => {
      const { associationId, billingCodes, email, isPersonUserActive, managerPersonId, personId, personFirstName, personLastName, personName, teamId } = person
      const isCurrentUser = currentUserPersonId === personId
      const shareTeamId = teamId && teamId === currentUserPersonId

      if (!isCurrentUser && isPersonUserActive) {
        const shareManager = managerPersonId && managerPersonId === currentUserManagerPersonId
        const isManagedByCurrentUser = managerPersonId && managerPersonId === currentUserPersonId
        const isManagerOfCurrentUser = personId === currentUserManagerPersonId

        if (isManagedByCurrentUser || shareTeamId) {
          const relationship = { isManagedByCurrentUser, isManagerOfCurrentUser, shareManager, shareTeamId }
          const relationships = Object.keys(relationship).reduce((ships, relationshipKey) => {
            const internalTeam = internalTeams.find((team) => team.teamId === teamId)
            const internalTeamName = internalTeam && internalTeam.teamName

            if (relationship[relationshipKey]) {
              ships.push(relationshipMap[relationshipKey](internalTeamName || teamId))
            }

            return ships
          }, [])
          const contact = { billingCodes, email, personFirstName, personLastName, personName, relationship, relationships, teamId }
          const action = 'read'
          const reportPath = 'profile'

          contact.href = `/${PATHS.INTERNAL.BASE}/${PATHS.INTERNAL.PERSON}?action=${action}&associationId=${associationId}&reportPath=${reportPath}&subId=${email}`

          contacts.push(contact)
        }
      }

      return contacts
    }, [])
  },
  managerContact(state, getters, rootState, rootGetters) {
    const { persons } = getters
    const currentUserManagerPersonId = rootGetters['auth/managerPersonId']
    const contacts = persons?.items || persons || []
    const manager = contacts.find((person) => person.personId === currentUserManagerPersonId)

    if (manager) {
      const { associationId, email } = manager
      const action = 'read'
      const reportPath = 'profile'

      manager.href = `/${PATHS.INTERNAL.BASE}/${PATHS.INTERNAL.PERSON}?action=${action}&associationId=${associationId}&reportPath=${reportPath}&subId=${email}`
    }

    return manager
  },
  mainContact() {
    // const isInternalUser = rootGetters['auth/isInternalUser']
    // const { managerContact } = getters

    return null // isInternalUser ? managerContact : null
  },

  timesheets() {
    return [
      { text: 'Last Week', value: -1 },
      { text: 'This Week', value: 0 },
      { text: 'Next Week', value: 1 },
    ]
  },

  didLastRefreshFail(state) {
    return state.dataRefreshReport?.last_refresh_failed
  },
  isDataRefreshing(state) {
    return state.dataRefreshReport?.refresh_active
  },
  indicateDataRefreshing(state, getters) {
    const { responses } = dataFreshness
    const { didLoadDataRefreshReportFail, isLoadingDataRefreshReport } = state
    const { isDataRefreshing, didLastRefreshFail } = getters

    if (isLoadingDataRefreshReport) return responses.loading()

    // Return false on fail to recover using line item dataRefreshedTime
    if (didLoadDataRefreshReportFail) return false

    if (didLastRefreshFail) return responses.recovering()

    if (!isDataRefreshing) return false

    return responses.refreshing()
  },
  isDataFreshnessView(state, getters) {
    const validReports = ['discrepancy', 'pacing']

    return validReports.includes(getters.currentPageName)
  },
  isValidDataRefreshedTime(state) {
    const { dataRefreshedTime } = state
    const errors = ['date not found', 'invalid date']
    const strDataRefreshedTime = String(dataRefreshedTime).toLowerCase()
    const isValidValue = !errors.includes(strDataRefreshedTime)

    return dataRefreshedTime && isValidValue
  },
  indicateDataRefreshed(state, getters) {
    const { responses } = dataFreshness
    const {
      indicateDataRefreshing,
      isDataFreshnessView,
      isValidDataRefreshedTime,
    } = getters
    const {
      isDataFresh,
      dataRefreshedTime,
      didLoadDataRefreshedTimeFail,
      isLoadingDataRefreshedTime,
    } = state

    if (indicateDataRefreshing) return false

    if (!isDataFreshnessView) return false

    if (isLoadingDataRefreshedTime) return responses.loading()

    if (didLoadDataRefreshedTimeFail) return responses.error()

    if (!dataRefreshedTime) return responses.pending()

    if (!isValidDataRefreshedTime) return responses.unknown(dataRefreshedTime)

    if (!isDataFresh) return responses.stale(dataRefreshedTime)

    return responses.success(dataRefreshedTime)
  },
}

export default GETTERS
